import Phaser from 'phaser';
import { bus, Events } from '@/core/events';
import { FixedStepper } from '@/core/clock';
import { SeededRng } from '@/core/rng';
import { DEFAULT_SETTINGS, type Settings } from '@/core/types';
import type { IAudioDirector } from '@/core/types/contracts';
import { Fader, duckGain } from './fades';
import { expandAmbience, fetchAudioManifest, pickFile, resolveAudioKey, type AudioCaps, type AudioEntry, type AudioManifest } from './manifest';
import { DEFAULT_SPATIAL, spatialize, type SpatialOptions } from './spatial';

type Sound = Phaser.Sound.BaseSound & { setVolume(v: number): unknown; setPan?(p: number): unknown; setRate?(r: number): unknown; volume: number };
interface WebAudioLike { context?: AudioContext; locked?: boolean }

interface MusicChannel { key: string; sound: Sound; fader: Fader; gain: number; stopping: boolean }
interface AmbienceLayer { key: string; sound: Sound; fader: Fader; gain: number; stopping: boolean }

export interface AudioDirectorOptions {
  /** URL of the generated manifest; defaults to `${BASE_URL}assets/audio/manifest.json`. */
  manifestUrl?: string;
  /** Prefix for manifest file paths; defaults to `${BASE_URL}assets/`. */
  assetBase?: string;
  settings?: Settings;
  seed?: number;
  spatial?: SpatialOptions;
  /**
   * Background warm-up once the manifest is in and the game has run for a moment: `false` = strictly
   * lazy; a predicate picks the keys; default = every music cue + the `ui-*` clicks, fetched one at a
   * time so scene loads keep their bandwidth. Music is kept as encoded bytes (≈1 MB each) and decoded
   * on first use; sfx are decoded right away.
   */
  prefetch?: boolean | ((key: string, entry: AudioEntry) => boolean);
}

export interface AudioDirectorState {
  unlocked: boolean;
  /** `audio:unlocked` has been emitted on the bus. */
  unlockEmitted: boolean;
  locked: boolean;
  contextState: string;
  manifestLoaded: boolean;
  music: string | null;
  musicVolume: number;
  ambience: { key: string; volume: number }[];
  duck: number;
  settings: Pick<Settings, 'musicVolume' | 'ambienceVolume' | 'sfxVolume' | 'muted'>;
  listener: { x: number; y: number };
  loaded: string[];
  /** Keys whose encoded bytes are already in memory (warm-up), decoded on first use. */
  warm: string[];
  /** Keys that failed to fetch/decode: muted for the rest of the session (never retried, never thrown). */
  failed: string[];
}

const MUSIC_FADE_MS = 900;
const AMBIENCE_FADE_MS = 1200;
const SFX_MIN_INTERVAL_MS = 35;
const DUCK_ATTACK_MS = 120; // must match duckGain()'s default attack
const WARM_UP_AFTER_MS = 1500; // game-time delay before the background warm-up starts
const DEFAULT_PREFETCH = (key: string, e: AudioEntry): boolean => e.kind === 'music' || key.startsWith('ui-');
const clamp01 = (v: number): number => Math.max(0, Math.min(1, v));

/**
 * Decode with the raw Web Audio API. Handles both the promise and the callback form, never throws and
 * never leaves a rejected promise behind (Phaser's own `decodeAudio` only console.error()s and lets the
 * rejection escape as an unhandled rejection).
 */
function decodeAudioData(ctx: AudioContext, bytes: ArrayBuffer): Promise<AudioBuffer | null> {
  return new Promise<AudioBuffer | null>((resolve) => {
    let settled = false;
    const done = (b: AudioBuffer | null) => { if (!settled) { settled = true; resolve(b); } };
    try {
      const p = ctx.decodeAudioData(bytes, (b) => done(b), () => done(null)) as Promise<AudioBuffer> | undefined;
      if (p && typeof p.then === 'function') p.then((b) => done(b), () => done(null));
    } catch { done(null); }
  });
}

/**
 * Game-global audio director on top of Phaser's Web Audio sound manager. Music crossfades and keeps
 * playing across scene transitions when the key is unchanged; ambience is a mix of looping beds with
 * per-layer volume; SFX go through a bus with optional listener-relative attenuation/pan; `duck()`
 * dips music+ambience for dialogue; `unlock()` resolves after the first user gesture and never throws.
 * Any cue that fails to fetch or decode is muted for the session (ARCHITECTURE §7) — no stall, no throw.
 */
export class AudioDirector implements IAudioDirector {
  private readonly stepper = new FixedStepper();
  private readonly rng: SeededRng;
  private readonly assetBase: string;
  private readonly manifestUrl: string;
  private readonly spatial: SpatialOptions;
  private readonly prefetch: ((key: string, entry: AudioEntry) => boolean) | null;
  private manifest: AudioManifest | null = null;
  private manifestPromise: Promise<AudioManifest | null> | null = null;
  private readonly loading = new Map<string, Promise<boolean>>();
  private readonly failed = new Set<string>();
  /** Encoded files fetched ahead of time (music), decoded when first needed. */
  private readonly bytes = new Map<string, ArrayBuffer>();
  private settings: Settings;
  private music: MusicChannel | null = null;
  private readonly outgoing: MusicChannel[] = [];
  private readonly ambience = new Map<string, AmbienceLayer>();
  /** The latest requested ambience mix — the only thing a decode continuation is allowed to start. */
  private wantedAmbience = new Map<string, number>();
  private ambienceFadeMs = AMBIENCE_FADE_MS;
  private pendingMusic: { key: string; fadeMs: number } | null = null;
  private pendingAmbience: { layers: { key: string; volume: number }[]; fadeMs: number } | null = null;
  private duckAmount = 0;
  private duckHoldMs = 0;
  private duckElapsed = -1; // -1 = idle
  private duckValue = 1;
  private unlocked = false;
  private unlockEmitted = false;
  private unlockPromise: Promise<void> | null = null;
  private listener = { x: 0, y: 0 };
  private lastSfxAt = new Map<string, number>();
  private readonly warnedKeys = new Set<string>();
  private clockMs = 0;
  private warmStarted = false;
  private destroyed = false;
  private readonly onStepBound = (_time: number, delta: number) => this.onStep(delta);
  private readonly onSettingsBound = (s: unknown) => this.applySettings(s as Settings);
  private readonly onPlayerMovedBound = (p: unknown) => { const m = p as { x: number; y: number }; if (m && Number.isFinite(m.x)) this.setListener(m.x, m.y); };
  private readonly gestureBound = () => this.tryResume();

  constructor(readonly game: Phaser.Game, opts: AudioDirectorOptions = {}) {
    const base = import.meta.env?.BASE_URL ?? './';
    this.assetBase = opts.assetBase ?? `${base}assets/`;
    this.manifestUrl = opts.manifestUrl ?? `${this.assetBase}audio/manifest.json`;
    this.settings = { ...DEFAULT_SETTINGS, ...(opts.settings ?? {}) };
    this.rng = new SeededRng(opts.seed ?? 1157).fork('audio');
    this.spatial = opts.spatial ?? DEFAULT_SPATIAL;
    this.prefetch = opts.prefetch === false ? null : typeof opts.prefetch === 'function' ? opts.prefetch : DEFAULT_PREFETCH;
    game.events.on(Phaser.Core.Events.STEP, this.onStepBound);
    bus.on(Events.SETTINGS_CHANGE, this.onSettingsBound);
    bus.on(Events.PLAYER_MOVED, this.onPlayerMovedBound);
    this.applySettings(this.settings);
    void this.loadManifest();
    void this.unlock();
  }

  // ------------------------------------------------------------------ IAudioDirector
  playMusic(rawKey: string | null, fadeMs = MUSIC_FADE_MS): void {
    if (this.destroyed) return;
    const key = rawKey === null || rawKey === '' ? null : resolveAudioKey(rawKey);
    if (key === null) { this.pendingMusic = null; if (this.music) this.retireMusic(this.music, fadeMs); this.music = null; return; }
    if (this.music && this.music.key === key && !this.music.stopping) { this.pendingMusic = null; this.music.fader.to(1, fadeMs); return; } // unchanged: keep playing
    // A track that is still fading out can be brought back without restarting.
    const idx = this.outgoing.findIndex((m) => m.key === key);
    if (idx >= 0) {
      this.pendingMusic = null;
      const back = this.outgoing.splice(idx, 1)[0] as MusicChannel;
      if (this.music) this.retireMusic(this.music, fadeMs);
      back.stopping = false; back.fader.to(1, fadeMs); this.music = back; return;
    }
    if (!this.unlocked) { this.pendingMusic = { key, fadeMs }; return; }
    this.pendingMusic = { key, fadeMs };
    void this.ensureLoaded(key).then((ok) => {
      // Only the latest request may start: a newer playMusic() replaced `pendingMusic`.
      if (this.destroyed || !ok || !this.pendingMusic || this.pendingMusic.key !== key) return;
      this.pendingMusic = null;
      if (this.music && this.music.key === key && !this.music.stopping) return;
      const entry = this.entry(key);
      const sound = this.game.sound.add(key, { loop: true, volume: 0 }) as Sound;
      if (this.music) this.retireMusic(this.music, fadeMs);
      this.music = { key, sound, fader: new Fader(0).to(1, fadeMs), gain: entry?.gain ?? 1, stopping: false };
      sound.play();
      this.applyMusicVolume(this.music);
    });
  }

  setAmbience(rawLayers: { key: string; volume: number }[], fadeMs = AMBIENCE_FADE_MS): void {
    if (this.destroyed) return;
    const layers = expandAmbience(rawLayers ?? []);
    if (!this.unlocked) { this.pendingAmbience = { layers, fadeMs }; return; }
    this.pendingAmbience = null;
    const wanted = new Map(layers.map((l) => [l.key, clamp01(l.volume)]));
    this.wantedAmbience = wanted; this.ambienceFadeMs = fadeMs;
    for (const [key, layer] of this.ambience) {
      if (!wanted.has(key)) { layer.stopping = true; layer.fader.to(0, fadeMs); }
    }
    for (const [key, volume] of wanted) {
      const existing = this.ambience.get(key);
      if (existing) { existing.stopping = false; existing.fader.to(volume, fadeMs); continue; }
      void this.ensureLoaded(key).then((ok) => {
        if (this.destroyed || !ok) return;
        // The mix may have changed while the bed was decoding: start it only if the LATEST mix still
        // wants it, at the latest volume — never the volume of the call that queued the decode.
        const target = this.wantedAmbience.get(key);
        if (target === undefined || this.ambience.has(key)) return;
        this.startAmbience(key, target, this.ambienceFadeMs);
      });
    }
  }
  sfx(rawKey: string, opts: { volume?: number; x?: number; y?: number; rate?: number } = {}): void {
    if (this.destroyed) return;
    if (!this.unlocked) {
      void this.unlock();
      return;
    }
    const key = resolveAudioKey(rawKey);
    const last = this.lastSfxAt.get(key) ?? -Infinity;
    if (this.clockMs - last < SFX_MIN_INTERVAL_MS) return;
    this.lastSfxAt.set(key, this.clockMs);
    void this.ensureLoaded(key).then((ok) => {
      if (!ok || this.destroyed || this.settings.muted) return;
      const entry = this.entry(key);
      let volume = (opts.volume ?? 1) * (entry?.gain ?? 1) * this.settings.sfxVolume;
      let pan = 0;
      if (opts.x !== undefined && opts.y !== undefined) {
        const s = spatialize({ x: opts.x, y: opts.y }, this.listener, this.spatial);
        if (s.gain <= 0.001) return;
        volume *= s.gain; pan = s.pan;
      }
      let rate = opts.rate ?? 1;
      if (opts.rate === undefined && key.startsWith('sfx-footstep')) rate = 1 + this.rng.range(-0.05, 0.05);
      const config: Phaser.Types.Sound.SoundConfig = { volume: clamp01(volume), rate, pan, loop: false };
      try { this.game.sound.play(key, config); } catch (e) { console.warn('[audio] sfx failed', key, e); }
    });
  }
  duck(amount: number, ms: number): void {
    const a = clamp01(amount), hold = Math.max(0, ms);
    const active = this.duckElapsed >= 0;
    if (a <= 0) {
      // Explicit release
      if (active) this.duckHoldMs = 0;
      return;
    }
    if (active && this.duckAmount >= a && this.duckElapsed < DUCK_ATTACK_MS + this.duckHoldMs) {
      // Already at least this deep and not yet releasing: just make sure the hold lasts `ms` from now.
      this.duckHoldMs = Math.max(this.duckHoldMs, Math.max(0, this.duckElapsed - DUCK_ATTACK_MS) + hold);
      return;
    }
    // Deeper request (or re-triggered during the release): restart the envelope FROM THE CURRENT GAIN so
    // the level never bumps back towards 1 before attacking again.
    const from = active ? this.duckValue : 1;
    const amt = Math.max(a, 1 - from);
    this.duckAmount = amt; this.duckHoldMs = hold;
    this.duckElapsed = amt > 0 ? Math.min(DUCK_ATTACK_MS, ((1 - from) / amt) * DUCK_ATTACK_MS) : 0;
  }

  unlock(): Promise<void> {
    if (this.unlocked) return Promise.resolve();
    if (this.unlockPromise) return this.unlockPromise;
    this.unlockPromise = new Promise<void>((resolve) => {
      const done = () => {
        if (this.unlocked) return;
        this.unlocked = true;
        this.removeGestureListeners();
        try { bus.emitTyped(Events.AUDIO_UNLOCKED, undefined); this.unlockEmitted = true; } catch { /* never throw */ }
        this.flushPending();
        resolve();
      };
      this.unlockDone = done;
      if (this.isRunning()) { done(); return; }
      try { this.game.sound.once(Phaser.Sound.Events.UNLOCKED, () => { if (this.isRunning()) done(); else this.tryResume(); }); } catch { /* no-op */ }
      this.addGestureListeners();
    });
    return this.unlockPromise;
  }

  // ------------------------------------------------------------------ extras (used by showcase / world)
  setListener(x: number, y: number): void { this.listener.x = x; this.listener.y = y; }
  isUnlocked(): boolean { return this.unlocked; }
  getManifest(): AudioManifest | null { return this.manifest; }
  loadManifest(): Promise<AudioManifest | null> {
    if (!this.manifestPromise) this.manifestPromise = fetchAudioManifest(this.manifestUrl).then((m) => { this.manifest = m; if (!m) console.warn(`[audio] manifest missing at ${this.manifestUrl}; run node tools/audio/synth.mjs`); return m; });
    return this.manifestPromise;
  }
  /** Keys the prefetch predicate selects that are neither decoded nor fetched yet (tooling / tests). */
  warmUpQueue(): string[] {
    const m = this.manifest, pick = this.prefetch; if (!m || !pick) return [];
    return Object.entries(m.entries).filter(([k, e]) => pick(k, e) && !this.game.cache.audio.exists(k) && !this.bytes.has(k) && !this.failed.has(k)).map(([k]) => k);
  }
  getState(): AudioDirectorState {
    const wa = this.game.sound as unknown as WebAudioLike;
    return {
      unlocked: this.unlocked, unlockEmitted: this.unlockEmitted, locked: !!this.game.sound.locked, contextState: wa.context?.state ?? 'n/a', manifestLoaded: !!this.manifest,
      music: this.music?.key ?? null, musicVolume: this.music ? this.music.sound.volume : 0,
      ambience: [...this.ambience.values()].filter((l) => !l.stopping).map((l) => ({ key: l.key, volume: +l.fader.value.toFixed(2) })),
      duck: +this.duckValue.toFixed(2),
      settings: { musicVolume: this.settings.musicVolume, ambienceVolume: this.settings.ambienceVolume, sfxVolume: this.settings.sfxVolume, muted: this.settings.muted },
      listener: { ...this.listener },
      loaded: this.manifest ? Object.keys(this.manifest.entries).filter((k) => this.game.cache.audio.exists(k)) : [],
      warm: [...this.bytes.keys()],
      failed: [...this.failed],
    };
  }
  stopAll(fadeMs = 300): void { this.playMusic(null, fadeMs); this.setAmbience([], fadeMs); }
  destroy(): void {
    this.destroyed = true;
    this.game.events.off(Phaser.Core.Events.STEP, this.onStepBound);
    bus.off(Events.SETTINGS_CHANGE, this.onSettingsBound);
    bus.off(Events.PLAYER_MOVED, this.onPlayerMovedBound);
    this.removeGestureListeners();
    for (const m of [this.music, ...this.outgoing]) if (m) this.safeDestroy(m.sound);
    for (const l of this.ambience.values()) this.safeDestroy(l.sound);
    this.music = null; this.outgoing.length = 0; this.ambience.clear(); this.wantedAmbience = new Map(); this.pendingMusic = null; this.pendingAmbience = null; this.bytes.clear();
  }

  // ------------------------------------------------------------------ internals
  private unlockDone: (() => void) | null = null;
  private isRunning(): boolean {
    const wa = this.game.sound as unknown as WebAudioLike;
    if (wa.context) return wa.context.state === 'running';
    return !this.game.sound.locked;
  }
  private tryResume(): void {
    const wa = this.game.sound as unknown as WebAudioLike;
    try {
      if (wa.context && wa.context.state !== 'running') { void wa.context.resume().then(() => { if (this.isRunning()) this.unlockDone?.(); }).catch(() => { /* retried on next gesture */ }); }
      else if (this.isRunning()) this.unlockDone?.();
    } catch { /* never throw */ }
  }
  private static readonly GESTURES = ['pointerdown', 'keydown', 'touchend', 'mousedown'];
  private addGestureListeners(): void { if (typeof document === 'undefined') return; for (const ev of AudioDirector.GESTURES) document.addEventListener(ev, this.gestureBound, { passive: true }); }
  private removeGestureListeners(): void { if (typeof document === 'undefined') return; for (const ev of AudioDirector.GESTURES) document.removeEventListener(ev, this.gestureBound); }
  private flushPending(): void {
    if (this.pendingMusic) { const p = this.pendingMusic; this.pendingMusic = null; this.playMusic(p.key, p.fadeMs); }
    if (this.pendingAmbience) { const p = this.pendingAmbience; this.pendingAmbience = null; this.setAmbience(p.layers, p.fadeMs); }
  }
  private startAmbience(key: string, volume: number, fadeMs: number): void {
    const entry = this.entry(key);
    const sound = this.game.sound.add(key, { loop: true, volume: 0 }) as Sound;
    const layer: AmbienceLayer = { key, sound, fader: new Fader(0).to(volume, fadeMs), gain: entry?.gain ?? 1, stopping: false };
    this.ambience.set(key, layer);
    sound.play();
    this.applyAmbienceVolume(layer);
  }
  private entry(key: string): AudioEntry | null { return this.manifest?.entries[key] ?? null; }
  private caps(): AudioCaps {
    const d = this.game.device.audio;
    return { ogg: !!d.ogg, opus: !!d.opus, m4a: !!d.m4a, mp3: !!d.mp3, webAudio: !!d.webAudio };
  }
  private warnOnce(key: string, msg: string, detail?: unknown): void {
    if (this.warnedKeys.has(key)) return;
    this.warnedKeys.add(key);
    if (detail === undefined) console.warn(msg); else console.warn(msg, detail);
  }
  /** Mute `key` for the rest of the session: later calls return false immediately (no refetch, no re-decode). */
  private giveUp(key: string): false { this.failed.add(key); return false; }

  /**
   * Resolve true once `key` is decodable in the audio cache. Lazy-fetches from the manifest and decodes
   * with the Web Audio API directly; a missing/corrupt file resolves false at once (and stays false).
   */
  ensureLoaded(key: string): Promise<boolean> {
    if (this.game.cache.audio.exists(key)) return Promise.resolve(true);
    if (this.failed.has(key)) return Promise.resolve(false);
    const inflight = this.loading.get(key); if (inflight) return inflight;
    const p = this.load(key).catch((e: unknown) => { this.warnOnce(key, `[audio] load failed for "${key}" — cue muted`, e); return this.giveUp(key); });
    this.loading.set(key, p);
    void p.finally(() => this.loading.delete(key));
    return p;
  }
  /** Manifest lookup → playable URL; warns once and mutes the key when there is nothing to play. */
  private async urlFor(key: string): Promise<{ url: string; file: string } | null> {
    const manifest = await this.loadManifest();
    const entry = manifest?.entries[key];
    if (!entry) { this.warnOnce(key, `[audio] unknown key "${key}"`); this.giveUp(key); return null; }
    const file = pickFile(entry.files, this.caps());
    if (!file) { this.warnOnce(key, `[audio] no playable format for "${key}"`); this.giveUp(key); return null; }
    return { url: `${this.assetBase}${file}`, file };
  }
  /** Encoded bytes for `key` (warm cache first); a failed fetch mutes the key. */
  private async fetchBytes(key: string, url: string): Promise<ArrayBuffer | null> {
    const warm = this.bytes.get(key);
    if (warm) { this.bytes.delete(key); return warm; }
    const res = await fetch(url);
    if (!res.ok) { this.warnOnce(key, `[audio] fetch ${url} → ${res.status}; "${key}" muted`); this.giveUp(key); return null; }
    return res.arrayBuffer();
  }
  private async load(key: string): Promise<boolean> {
    const target = await this.urlFor(key); if (!target) return false;
    const { url, file } = target;
    const wa = this.game.sound as unknown as WebAudioLike;
    if (!wa.context) return this.loadViaScene(key, url);
    const bytes = await this.fetchBytes(key, url); if (!bytes) return false;
    if (this.game.cache.audio.exists(key)) return true; // a scene loader got there first
    const decoded = await decodeAudioData(wa.context, bytes);
    if (!decoded) { this.warnOnce(key, `[audio] decode failed for "${key}" (${file}) — cue muted`); return this.giveUp(key); }
    if (this.destroyed) return false;
    this.game.cache.audio.add(key, decoded);
    try { this.game.sound.emit(Phaser.Sound.Events.DECODED, key); } catch { /* NoAudio manager */ }
    return true;
  }
  /** HTML5-audio fallback (no AudioContext): let a live scene's loader fetch the file. */
  private loadViaScene(key: string, url: string): Promise<boolean> {
    const scene = this.game.scene.getScenes(true)[0];
    if (!scene) return Promise.resolve(false);
    return new Promise<boolean>((resolve) => {
      scene.load.audio(key, url);
      scene.load.once(`${Phaser.Loader.Events.FILE_COMPLETE}-audio-${key}`, () => resolve(true));
      scene.load.once(Phaser.Loader.Events.COMPLETE, () => resolve(this.game.cache.audio.exists(key) || this.giveUp(key)));
      scene.load.start();
    });
  }
  /**
   * Background warm-up so the first cue of a scene does not wait on the network; one file at a time.
   * Music (≈1 MB encoded, ≈20 MB decoded each) is kept as bytes and decoded on first use; short cues
   * are decoded immediately.
   */
  private async warmUp(): Promise<void> {
    for (const key of this.warmUpQueue()) {
      if (this.destroyed) return;
      if (this.entry(key)?.kind !== 'music') { await this.ensureLoaded(key); continue; }
      if (this.loading.has(key) || this.bytes.has(key) || this.game.cache.audio.exists(key)) continue;
      try {
        const target = await this.urlFor(key); if (!target) continue;
        const b = await this.fetchBytes(key, target.url);
        if (b && !this.destroyed && !this.game.cache.audio.exists(key)) this.bytes.set(key, b);
      } catch (e) { this.warnOnce(key, `[audio] warm-up fetch failed for "${key}"`, e); }
    }
  }

  private applySettings(s: Settings): void {
    this.settings = { ...this.settings, ...s };
    try { this.game.sound.setMute(!!this.settings.muted); } catch { /* NoAudio manager */ }
    if (this.music) this.applyMusicVolume(this.music);
    for (const m of this.outgoing) this.applyMusicVolume(m);
    for (const l of this.ambience.values()) this.applyAmbienceVolume(l);
  }
  private retireMusic(m: MusicChannel, fadeMs: number): void { m.stopping = true; m.fader.to(0, fadeMs); this.outgoing.push(m); }
  private applyMusicVolume(m: MusicChannel): void { this.setVol(m.sound, m.fader.value * m.gain * this.settings.musicVolume * this.duckValue); }
  private applyAmbienceVolume(l: AmbienceLayer): void { this.setVol(l.sound, l.fader.value * l.gain * this.settings.ambienceVolume * (1 - (1 - this.duckValue) * 0.6)); }
  private setVol(sound: Sound, v: number): void { const c = clamp01(v); if (Math.abs(sound.volume - c) > 0.002) { try { sound.setVolume(c); } catch { /* destroyed */ } } }
  private safeDestroy(sound: Sound): void { try { sound.stop(); sound.destroy(); } catch { /* already gone */ } }

  private onStep(delta: number): void {
    if (this.destroyed) return;
    this.stepper.tick(delta, (dt) => this.step(dt));
  }
  private step(dt: number): void {
    this.clockMs += dt;
    if (!this.unlocked && this.isRunning()) this.unlockDone?.();
    if (!this.warmStarted && this.prefetch && this.manifest && this.clockMs >= WARM_UP_AFTER_MS) { this.warmStarted = true; void this.warmUp(); }
    // duck envelope
    if (this.duckElapsed >= 0) {
      this.duckElapsed += dt;
      this.duckValue = duckGain(this.duckElapsed, this.duckAmount, this.duckHoldMs);
      if (this.duckValue >= 1 && this.duckElapsed > this.duckHoldMs) { this.duckElapsed = -1; this.duckValue = 1; }
    }
    if (this.music) { this.music.fader.step(dt); this.applyMusicVolume(this.music); }
    for (let i = this.outgoing.length - 1; i >= 0; i--) {
      const m = this.outgoing[i] as MusicChannel;
      const done = m.fader.step(dt); this.applyMusicVolume(m);
      if (done && m.fader.value <= 0) { this.outgoing.splice(i, 1); this.safeDestroy(m.sound); }
    }
    for (const [key, l] of this.ambience) {
      const done = l.fader.step(dt); this.applyAmbienceVolume(l);
      if (l.stopping && done && l.fader.value <= 0) { this.ambience.delete(key); this.safeDestroy(l.sound); }
    }
  }
}
